import {
  Audio,
  AudiosPaginationType,
  CloneVoiceArgs,
  CloneVoiceResponse,
  ConvertToAudioArgs,
  ConvertToAudioResponse,
  EndPoints,
  GenerateAudioScriptArgs,
  GenerateAudioScriptResponse,
  GenerateAudioSuggestionsArgs,
  GenerateAudioSuggestionsResponse,
  GenerateAudioSummaryArgs,
  GenerateAudioSummaryResponse,
  GenerateCoverImageArgs,
  GenerateCoverImageResponse,
  GenerateShortArgs,
  GenerateShortResponse,
  Genres,
  GenresPaginationType,
  GetJobStatusArgs,
  GetJobStatusResponse,
  GroupedAudiosResponse,
  HttpMethods,
  RegenerateAudioParagraphArgs,
  RegenerateAudioParagraphResponse,
  RegenerateAudioTrackArgs,
  RegenerateAudioTrackResponse,
  SubmitAudioArgs,
  SubmitAudioResponse,
} from 'interfaces';
import {normalize, schema} from 'normalizr';
import {Response} from 'redaxios';
import {requestHandler} from 'services/api';
import {unwrapAPIError} from 'utils';
import {v4 as uuidv4} from 'uuid';

const cloneVoice = async (
  data: CloneVoiceArgs,
): Promise<
  Response<{
    message: CloneVoiceResponse;
  }>
> => {
  try {
    const formData = new FormData();
    formData.append('audio', data.file, 'recording-' + uuidv4() + '.wav');
    const result = await requestHandler<
      {
        status: number;
        message: CloneVoiceResponse;
      },
      typeof formData
    >({
      method: HttpMethods.POST,
      url: `${EndPoints.CloneVoice}` as unknown as EndPoints,
      data: formData,
    });

    if (result.data.status !== 200) {
      return Promise.reject(result.data.message);
    }

    data.onSuccess?.(result.data.message);

    return result;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const regenerateAudioParagraph = async (
  data: RegenerateAudioParagraphArgs,
): Promise<
  Response<{
    message: RegenerateAudioParagraphResponse;
  }>
> => {
  try {
    const result = await requestHandler<
      {
        status: number;
        message: RegenerateAudioParagraphResponse;
      },
      typeof data
    >({
      method: HttpMethods.PATCH,
      url: `${EndPoints.RegenerateAudioParagraph}` as unknown as EndPoints,
      data,
    });

    if (result.data.status !== 200) {
      return Promise.reject(result.data.message);
    }

    data.onSuccess?.(result.data.message);

    return result;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const regenerateCoverImage = async (
  data: GenerateCoverImageArgs,
): Promise<
  Response<{
    message: GenerateCoverImageResponse;
  }>
> => {
  try {
    const result = await requestHandler<
      {
        status: number;
        message: GenerateCoverImageResponse;
      },
      typeof data
    >({
      method: HttpMethods.POST,
      url: `${EndPoints.RegenerateCoverImage}` as unknown as EndPoints,
      data,
    });

    if (result.data.status !== 200) {
      return Promise.reject(result.data.message);
    }

    data.onSuccess?.(result.data.message);

    return result;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const generateAudioSuggestions = async (
  data: GenerateAudioSuggestionsArgs,
): Promise<
  Response<{
    message: GenerateAudioSuggestionsResponse;
  }>
> => {
  try {
    const result = await requestHandler<
      {
        status: number;
        message: GenerateAudioSuggestionsResponse;
      },
      typeof data
    >({
      method: HttpMethods.POST,
      url: `${EndPoints.GenerateAudioSuggestions}` as unknown as EndPoints,
      data,
    });

    if (result.data.status !== 200) {
      return Promise.reject(result.data.message);
    }

    data.onSuccess?.(result.data.message);

    return result;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const submitAudio = async (
  data: SubmitAudioArgs,
): Promise<
  Response<{
    message: SubmitAudioResponse;
  }>
> => {
  try {
    const result = await requestHandler<
      {
        status: number;
        message: SubmitAudioResponse;
      },
      typeof data
    >({
      method: HttpMethods.PATCH,
      url: `${EndPoints.SubmitAudio}` as unknown as EndPoints,
      data,
    });

    if (result.data.status !== 200) {
      return Promise.reject(result.data.message);
    }

    data.onSuccess?.(result.data.message);

    return result;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const generateAudioScript = async (
  data: GenerateAudioScriptArgs,
): Promise<
  Response<{
    message: GenerateAudioScriptResponse;
  }>
> => {
  try {
    const result = await requestHandler<
      {
        status: number;
        message: GenerateAudioScriptResponse;
      },
      typeof data.params
    >({
      method: HttpMethods.POST,
      url: `${EndPoints.GenerateAudioScript}` as unknown as EndPoints,
      data: data.params,
    });

    if (result.data.status !== 200) {
      return Promise.reject(result.data.message);
    }

    data.onSuccess?.(result.data.message);

    return result;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const getAudios = async ({
  providerId,
  page,
  pageSize,
}: {
  providerId: string;
  page: number;
  pageSize: number;
}): Promise<
  Response<{
    message: {audios: Audio[]; pagination: AudiosPaginationType};
  }>
> => {
  try {
    const result = await requestHandler<
      {
        status: number;
        message: {audios: Audio[]; pagination: AudiosPaginationType};
      },
      void
    >({
      method: HttpMethods.GET,
      url: `${EndPoints.GetAudios}/${providerId}?page=${page}&limit=${pageSize}` as unknown as EndPoints,
    });

    if (result.data.status !== 200) {
      return Promise.reject(result.data.message);
    }

    return result;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const audio = new schema.Entity('audios', {}, {idAttribute: '_id'});
const genre = new schema.Entity(
  'genres',
  {
    audios: [audio],
  },
  {
    idAttribute: value => value.genre,
  },
);

const arrayOfGenres = new schema.Array(genre);

// Get grouped audios
const getGroupedAudios = async ({
  providerId,
  page,
  pageSize,
  rowLimit,
}: {
  providerId: string;
  page?: number;
  pageSize?: number;
  rowLimit?: number;
}): Promise<GroupedAudiosResponse> => {
  try {
    const result = await requestHandler<
      {
        status: number;
        message: {genres: Genres[]; pagination: GenresPaginationType};
      },
      void
    >({
      method: HttpMethods.GET,
      url: `${EndPoints.getGroupedAudios}/${providerId}?page=${page}&limit=${pageSize}&rowLimit=${rowLimit}` as unknown as EndPoints,
    });

    if (result.data.status !== 200) {
      return Promise.reject(result.data.message);
    }

    const {
      message: {genres, pagination},
    } = result.data;

    const normalizedGenresData = normalize(genres, arrayOfGenres);

    const genresEntity = normalizedGenresData.entities.genres ?? {};
    const audioEntity = normalizedGenresData.entities.audios ?? {};

    const genresRes = Object.entries(genresEntity).reduce(
      (acc, [genreName, genreData]) => {
        acc[genreName] = {
          ...genreData,
          audios: (genreData.audios as string[]).map(
            (audioId: string) => audioEntity[audioId],
          ),
        };
        return acc;
      },
      {} as Record<string, any>,
    );

    return {
      normalizedGenres: genresRes ?? {},
      pagination,
    };
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

// Get audios by genre
const getAudiosByGenre = async ({
  providerId,
  genre,
  page,
  pageSize,
}: {
  providerId: string;
  genre: string;
  page: number;
  pageSize: number;
}): Promise<
  Response<{
    message: Genres;
  }>
> => {
  try {
    const result = await requestHandler<
      {
        status: number;
        message: Genres;
      },
      void
    >({
      method: HttpMethods.GET,
      url: `${
        EndPoints.getAudiosByGenre
      }/${providerId}?genre=${encodeURIComponent(
        genre,
      )}&page=${page}&limit=${pageSize}` as unknown as EndPoints,
    });

    if (result.data.status !== 200) {
      return Promise.reject(result.data.message);
    }

    return result;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const regenerateAudioTrack = async (
  data: RegenerateAudioTrackArgs,
): Promise<
  Response<{
    message: RegenerateAudioTrackResponse;
  }>
> => {
  try {
    const result = await requestHandler<{
      status: number;
      message: RegenerateAudioTrackResponse;
    }>({
      method: HttpMethods.PATCH,
      url: `${EndPoints.RegenerateAudioTrack}/${data.audioId}` as unknown as EndPoints,
    });

    if (result.data.status !== 200) {
      return Promise.reject(result.data.message);
    }

    data.onSuccess?.(result.data.message);

    return result;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const deleteAudio = async (
  audioId: string,
  onSuccess?: () => void,
): Promise<void> => {
  try {
    await requestHandler<void>({
      method: HttpMethods.DELETE,
      url: `${EndPoints.DeleteAudio}${audioId}` as unknown as EndPoints,
    });
    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const generateAudioCoverImage = async (
  data: GenerateCoverImageArgs,
): Promise<
  Response<{
    message: GenerateCoverImageResponse;
  }>
> => {
  try {
    const result = await requestHandler<
      {
        status: number;
        message: GenerateCoverImageResponse;
      },
      typeof data
    >({
      method: HttpMethods.POST,
      url: `${EndPoints.GenerateCoverImage}` as unknown as EndPoints,
      data,
    });

    if (result.data.status !== 200) {
      return Promise.reject(result.data.message);
    }

    data.onSuccess?.(result.data.message);

    return result;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const convertToAudio = async (
  data: ConvertToAudioArgs,
): Promise<
  Response<{
    message: ConvertToAudioResponse;
  }>
> => {
  try {
    const result = await requestHandler<
      {
        status: number;
        message: ConvertToAudioResponse;
      },
      typeof data
    >({
      method: HttpMethods.POST,
      url: `${EndPoints.ConvertToAudio}` as unknown as EndPoints,
      data,
    });

    if (result.data.status !== 200) {
      return Promise.reject(result.data.message);
    }

    data.onSuccess?.(result.data.message);

    return result;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const getJobStatus = async (
  data: GetJobStatusArgs,
): Promise<
  Response<{
    message: GetJobStatusResponse;
  }>
> => {
  try {
    const result = await requestHandler<
      {
        status: number;
        message: GetJobStatusResponse;
      },
      typeof data
    >({
      method: HttpMethods.GET,
      url: `${data.endpoint}/${data.jobId}` as unknown as EndPoints,
    });

    if (result.data.status !== 200) {
      return Promise.reject(result.data.message);
    }

    data.onSuccess?.(result.data.message);

    return result;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const getAudioById = async (
  audioId: string,
  onSuccess?: (data: Audio) => void,
): Promise<
  Response<{
    message: Audio;
  }>
> => {
  try {
    const result = await requestHandler<
      {
        status: number;
        message: Audio;
      },
      void
    >({
      method: HttpMethods.GET,
      url: `${EndPoints.GetAudioById}/${audioId}` as unknown as EndPoints,
    });

    if (result.data.status !== 200) {
      return Promise.reject(result.data.message);
    }

    onSuccess?.(result.data.message);

    return result;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const generateAudioSummary = async (
  data: GenerateAudioSummaryArgs,
  onSuccess?: (response: GenerateAudioSummaryResponse) => void,
): Promise<
  Response<{
    message: GenerateAudioSummaryResponse;
  }>
> => {
  try {
    const result = await requestHandler<
      {
        status: number;
        message: GenerateAudioSummaryResponse;
      },
      typeof data
    >({
      method: HttpMethods.POST,
      url: `${EndPoints.GenerateAudioSummary}` as unknown as EndPoints,
      data,
    });

    if (result.data.status !== 200) {
      return Promise.reject(result.data.message);
    }

    onSuccess?.(result.data.message);

    return result;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const generateShort = async (
  data: GenerateShortArgs,
): Promise<
  Response<{
    message: GenerateShortResponse;
  }>
> => {
  try {
    const result = await requestHandler<
      {
        status: number;
        message: GenerateShortResponse;
      },
      typeof data
    >({
      method: HttpMethods.POST,
      url: `${EndPoints.GenerateShort}/${data.audioId}` as unknown as EndPoints,
      data,
    });

    if (result.data.status !== 200) {
      return Promise.reject(result.data.message);
    }

    return result;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

export const WellniteAudiosService = {
  cloneVoice,
  generateAudioScript,
  regenerateAudioParagraph,
  regenerateCoverImage,
  submitAudio,
  generateAudioSuggestions,
  getAudios,
  regenerateAudioTrack,
  deleteAudio,
  generateAudioCoverImage,
  convertToAudio,
  getJobStatus,
  getAudioById,
  generateAudioSummary,
  generateShort,
  getAudiosByGenre,
  getGroupedAudios,
};
